import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as PayrollIcon,
  AccessTime as AttendanceIcon,
  EventNote as LeaveIcon,
} from '@mui/icons-material';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { employeeService } from '../services/employeeService';
import { payrollService } from '../services/payrollService';
import { attendanceService } from '../services/attendanceService';
import { leaveRequestService } from '../services/leaveRequestService';
import { formatCurrency } from '../utils/currencyutils';
import { getFirstDayOfMonth, getLastDayOfMonth } from '../utils/dateUtils';
import { Employee, Payroll } from '../types';
import LeaveTrackingDashboard from '../components/LeaveTrackingDashboard';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  // Removed unused state variables
  const [stats, setStats] = useState({
    activeEmployees: 0,
    monthlyPayroll: 0,
    presentToday: 0,
    pendingLeaveRequests: 0,
    totalHoursWorked: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('Dashboard: Starting data fetch...');
        
        // Get current month date range
        const startDate = getFirstDayOfMonth();
        const endDate = getLastDayOfMonth();
        console.log('Dashboard: Date range:', { startDate, endDate });

        // Fetch data with individual error handling
        let employeesData: Employee[] = [];
        let payrollsData: Payroll[] = [];
        let attendanceData: any[] = [];
        let leaveRequestsData: any[] = [];

        try {
          employeesData = await employeeService.getAll();
          console.log('Dashboard: Employees fetched:', employeesData.length);
        } catch (err) {
          console.error('Dashboard: Error fetching employees:', err);
        }

        try {
          payrollsData = await payrollService.getByDateRange(startDate, endDate);
          console.log('Dashboard: Payrolls fetched:', payrollsData.length);
        } catch (err) {
          console.error('Dashboard: Error fetching payrolls:', err);
        }

        try {
          attendanceData = await attendanceService.getByDateRange(startDate, endDate);
          console.log('Dashboard: Attendance records fetched:', attendanceData.length);
        } catch (err) {
          console.error('Dashboard: Error fetching attendance:', err);
        }

        try {
          leaveRequestsData = await leaveRequestService.getAll();
          console.log('Dashboard: Leave requests fetched:', leaveRequestsData.length);
        } catch (err) {
          console.error('Dashboard: Error fetching leave requests:', err);
        }

        // Calculate current month and year
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const today = new Date().toISOString().split('T')[0];
        console.log('Dashboard: Today date:', today);
        
        // Filter today's attendance
        const todayAttendance = attendanceData.filter(att => att.date === today);
        console.log('Dashboard: Today attendance records:', todayAttendance.length);
        
        // Calculate monthly payroll
        const monthlyPayroll = payrollsData
          .filter(p => {
            const payrollDate = new Date(p.payment_date);
            return payrollDate.getMonth() === currentMonth && payrollDate.getFullYear() === currentYear;
          })
          .reduce((sum, p) => sum + p.net_pay, 0);

        const totalHoursWorked = payrollsData
          .filter(p => {
            const payrollDate = new Date(p.payment_date);
            return payrollDate.getMonth() === currentMonth && payrollDate.getFullYear() === currentYear;
          })
          .reduce((sum, p) => sum + (p.total_hours_worked || 0), 0);

        // Calculate stats
        const activeEmployees = employeesData.filter(emp => emp.status === 'active').length;
        const presentToday = todayAttendance.filter(att => att.status === 'present').length;
        const pendingLeaveRequests = leaveRequestsData.filter(req => req.status === 'pending').length;

        console.log('Dashboard: Calculated stats:', {
          activeEmployees,
          monthlyPayroll,
          presentToday,
          pendingLeaveRequests,
          totalHoursWorked
        });

        // Update state
        setEmployees(employeesData);
        setPayrolls(payrollsData);
        
        setStats({
          activeEmployees,
          monthlyPayroll,
          presentToday,
          pendingLeaveRequests,
          totalHoursWorked,
        });
      } catch (error) {
        console.error('Dashboard: Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);



  // Department distribution data for pie chart
  const departmentData = {
    labels: [...new Set(employees.map(emp => emp.department))],
    datasets: [
      {
        label: 'Employees by Department',
        data: [...new Set(employees.map(emp => emp.department))].map(
          dept => employees.filter(emp => emp.department === dept).length
        ),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Monthly payroll data for bar chart
  const payrollData = {
    labels: ['Salaries', 'Overtime', 'Bonuses', 'Deductions', 'Taxes'],
    datasets: [
      {
        label: 'Amount (USD)',
        data: [
          payrolls.reduce((sum, p) => sum + p.basic_salary, 0),
          payrolls.reduce((sum, p) => sum + p.overtime_pay, 0),
          payrolls.reduce((sum, p) => sum + p.bonuses, 0),
          payrolls.reduce((sum, p) => sum + (p.total_deductions || 0), 0),
          payrolls.reduce((sum, p) => sum + (p.withholding_tax || 0), 0),
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
    ],
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, px: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #1e40af 100%)',
          color: 'white',
          p: 4,

          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(37, 99, 235, 0.2), 0 8px 16px rgba(124, 58, 237, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            opacity: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              color: 'white',
              mb: 1,
            }}
          >
            Dashboard
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            Welcome back! Here's what's happening with your organization today.
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #1d4ed8 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',

              boxShadow: '0 12px 24px rgba(37, 99, 235, 0.25), 0 4px 8px rgba(37, 99, 235, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 16px 32px rgba(37, 99, 235, 0.3), 0 8px 16px rgba(37, 99, 235, 0.2)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 120,
                height: 120,
                background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%)',

                transform: 'translate(40px, -40px)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <PeopleIcon sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                Active Employees
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.activeEmployees}
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 50%, #059669 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',

              boxShadow: '0 12px 24px rgba(16, 185, 129, 0.25), 0 4px 8px rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 16px 32px rgba(16, 185, 129, 0.3), 0 8px 16px rgba(16, 185, 129, 0.2)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 120,
                height: 120,
                background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%)',

                transform: 'translate(40px, -40px)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <PayrollIcon sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                Monthly Payroll
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {formatCurrency(stats.monthlyPayroll)}
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #6d28d9 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',

              boxShadow: '0 12px 24px rgba(124, 58, 237, 0.25), 0 4px 8px rgba(124, 58, 237, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 16px 32px rgba(124, 58, 237, 0.3), 0 8px 16px rgba(124, 58, 237, 0.2)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 120,
                height: 120,
                background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%)',

                transform: 'translate(40px, -40px)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <AttendanceIcon sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                Present Today
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.presentToday}
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #d97706 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',

              boxShadow: '0 12px 24px rgba(245, 158, 11, 0.25), 0 4px 8px rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 16px 32px rgba(245, 158, 11, 0.3), 0 8px 16px rgba(245, 158, 11, 0.2)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 120,
                height: 120,
                background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%)',

                transform: 'translate(40px, -40px)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <LeaveIcon sx={{ fontSize: 40, mb: 2, opacity: 0.9 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                Pending Leave Requests
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.pendingLeaveRequests}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom component="div">
              Department Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <Pie data={departmentData} options={{ maintainAspectRatio: false }} />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom component="div">
              Monthly Payroll Breakdown
            </Typography>
            <Box sx={{ height: 300 }}>
              <Bar 
                data={payrollData} 
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }} 
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Leave Tracking Dashboard */}
      <Box sx={{ mb: 4 }}>
        <LeaveTrackingDashboard />
      </Box>
    </Box>
  );
};

export default Dashboard;